/**
 * rule-action-meta — single source of truth for a rule action's label + icon.
 * Shared by the Rules-tab ACTIONS picker and the preview's per-action icons so
 * the same glyph means the same thing in both places.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  Ban,
  Hourglass,
  Pause,
  Play,
  Power,
  PlusCircle,
  Scale,
  Trash2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import type { RuleAction } from '@trafficpulse/shared';

export interface RuleActionMeta {
  value: RuleAction;
  label: string;
  Icon: LucideIcon;
  /** Not yet functional — shown disabled in the picker, never surfaced as an icon. */
  soon?: boolean;
  /** Tailwind classes for the icon chip in the preview (tone matches the action). */
  tone: string;
}

/** What the picker's trigger and items draw — the Automation's rule actions
 *  use the same parts with their own values (one look for both features). */
export type RuleActionLook = Pick<RuleActionMeta, 'label' | 'Icon' | 'tone' | 'soon'> & { value: string };

/** Display order = picker order. */
export const RULE_ACTIONS: RuleActionMeta[] = [
  { value: 'exclude', label: 'Exclude', Icon: Ban, tone: 'bg-destructive/10 text-destructive' },
  { value: 'weight', label: 'Weight', Icon: Scale, tone: 'bg-primary/10 text-primary' },
  // MD-1147: the partner-cap verbs act on a matched PATH's offers.
  { value: 'pause', label: 'Pause capped offers', Icon: Pause, tone: 'bg-warning/10 text-warning' },
  { value: 'reactivate', label: 'Reactivate capped offers', Icon: Play, tone: 'bg-success/10 text-success' },
  { value: 'switch-off', label: 'Switch off', Icon: Power, soon: true, tone: 'bg-muted text-muted-foreground' },
  { value: 'set-to-queue', label: 'Set to queue', Icon: Undo2, tone: 'bg-primary/10 text-primary' },
  { value: 'set-to-waiting', label: 'Set to waiting', Icon: Hourglass, tone: 'bg-muted text-muted-foreground' },
  { value: 'eliminate', label: 'Eliminate', Icon: Trash2, tone: 'bg-destructive/10 text-destructive' },
  { value: 'add-element', label: 'Add new element', Icon: PlusCircle, tone: 'bg-success/10 text-success' },
];

export const RULE_ACTION_META: Partial<Record<RuleAction, RuleActionMeta>> =
  Object.fromEntries(RULE_ACTIONS.map((a) => [a.value, a]));
